/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef } from 'react';
import isInteger from 'lodash/isInteger';

import {
    gaProductObject,
    gaGenerateCheckoutEvent,
    CART_GA_PRODUCT_REMOVE_FROM_CART_KEY,
    breakApartAndGetIdFromProductId,
} from 'sarsaparilla';

import {
    isRecommendationReferrer,
    findRecommendationDataInDataLayer,
    createRecommendationDataFromReferrer,
} from './recommendationUtils';
import { clearBuyNowCheckoutMarker, getBuyNowCheckoutMarker } from './buyNowTracking';

declare const window: Window & { dataLayer: Record<string, unknown>[] };

const INVENTORY_LOTTERY_CAMPING = 'LOTTERY_CAMPING';
const INVENTORY_CAMPING = 'CAMPING';
const INVENTORY_POS = 'POS';
const INVENTORY_TICKET = 'TICKET';
const INVENTORY_TIMED_ENTRY = 'TIMED_ENTRY';

type Category =
    | typeof INVENTORY_LOTTERY_CAMPING
    | typeof INVENTORY_CAMPING
    | typeof INVENTORY_POS
    | typeof INVENTORY_TICKET
    | typeof INVENTORY_TIMED_ENTRY;

interface CartLineItem {
    adjustment_type: string;
    affected_skus: string | null;
    current: boolean;
    description: string;
    fee_model_version: number;
    fee_type: string;
    line_item_id: string;
    quantity: number;
    reference_id: string;
    sku: string;
    subtotal: string;
    unit_price: string;
}

interface OrderDetails {
    camping_type_of_use: string;
    campsite_id: string;
    check_in: string;
    check_in_time: string;
    check_out: string;
    check_out_time: string;
    description: string;
    facility_id: string;
    facility_type: string;
    img_url: string;
    location: string;
    location_id: string;
    name?: string; // MIG: Do we add this here or make a new interface?
    nights: string;
    num_occupants: string;
    num_vehicles: string;
    permit_print_start_date: string;
    primary_equipment: string;
    primary_occupant: string;
    product_category: string;
    product_description: string;
    product_name: string;
    reservation_start: string;
    recarea: string;
    tour_id?: string; // MIG: Do we add this here or make a new interface?
}

interface Reservation {
    adjustment: string;
    balance_due_amount: string;
    balance_due_date: string | null;
    cart_line_items: CartLineItem[];
    creation_source: string;
    discount: string;
    errors: string[];
    fee_model: string;
    inventory_type: string;
    order_details: OrderDetails;
    order_id: string;
    quantity: number;
    rec_area_id: string;
    rec_area_name: string;
    recommendation_referrer: string;
    reservation_id: string;
    reservation_number: string;
    reservation_status: string;
    subtotal: string;
    state_code: string;
    tax: string;
    tenant_id: string;
    total: string;
}

interface Steps {
    [key: string]: string;
}

interface GAItemEvent {
    itemLevel:
        'Gateway' | 'Facility' | 'Inventory' | 'Registration Detail' | 'Pass Selection';
    facilityId: string;
    itemCategory?: string;
    itemId?: string;
    itemName?: string;
    inventoryId?: string;
    inventoryName?: string;
    price?: number;
    eventType?: string;
    isDataLoading?: boolean;
    typeOfUse?: string | string[]; // Campsite Type of Use
}

interface ProductItem {
    index: number;
    item_brand?: string;
    item_id: string;
    item_name: string;
    item_level: string;
    agency_name: string;
    state_code: string;
    rec_area_id: string;
    rec_area_name: string;
    facility_id?: string;
    facility_name?: string;
    inventory_id?: string;
    inventory_name?: string;
    item_category?: string;
    price?: number;
    type_of_use?: string | string[];
    recommendation_referrer?: string;
}

const getRecAreaInfoNotFound = (facilityId: string) => ({
    agencyName: 'Not Found',
    facilityId,
    facilityName: 'Not Found',
    recAreaId: 'Not Found',
    recAreaName: 'Not Found',
    stateCode: 'Not Found',
});

/**
 * Standardized rec area info for GA tracking events.
 * Uses snake_case to match GA event field naming conventions.
 */
export interface RecAreaTrackingInfo {
    agency_name: string;
    state_code: string;
    rec_area_id: string;
    rec_area_name: string;
}

const REC_AREA_TRACKING_INFO_DEFAULT: RecAreaTrackingInfo = {
    agency_name: '',
    state_code: '',
    rec_area_id: '',
    rec_area_name: '',
};

const getInventoryNameFromReservation = (
    category: Category,
    orderDetails: OrderDetails
) => {
    let inventoryName = null;
    switch (category) {
        case INVENTORY_CAMPING:
            inventoryName = `Site: ${orderDetails.product_description} - Loop: ${orderDetails.product_name}`;
            break;
        default:
            inventoryName = `${orderDetails.product_description} - ${orderDetails.product_name}`;
    }
    return inventoryName;
};

/**
 * gaExtractProductIdAndCategoryFromReservation - gets a productId and category from a cart reservation
 * @param {Object} reservation - a cart-based reservation
 * @returns { productId, category } - returns an id and category for a reservation
 */
export const gaExtractProductIdAndCategoryFromReservation = (
    reservation: Reservation
) => {
    let productId = reservation?.order_details?.location_id;
    const category = reservation?.inventory_type as Category;
    if (category) {
        switch (category) {
            case INVENTORY_TICKET:
            case INVENTORY_TIMED_ENTRY:
                productId += `|${reservation?.order_details?.tour_id}`;
                break;
            case INVENTORY_CAMPING:
            case INVENTORY_LOTTERY_CAMPING:
                productId += `|${reservation?.order_details?.campsite_id}`;
                break;
            default:
                break;
        }
    }
    return {
        productId,
        category,
    };
};

// Attempt to prevent duplicate fetches
const fetchCache = new Map();

const fetchRecAreaInfo = async (
    facilityId: string
): Promise<{
    agencyName: string;
    facilityId: string;
    facilityName: string;
    recAreaId: string;
    recAreaName: string;
    stateCode: string;
}> => {
    if (fetchCache.has(facilityId)) {
        return fetchCache.get(facilityId);
    }

    // for each reservation, use the search api to get the info
    // this will be used to report the rec area info to google analytics
    try {
        const response = await fetch(
            `${process.env.API}/search?fq=entity_id:${facilityId}`
        );
        const { results } = await response.json();
        if (!results?.length) {
            // eslint-disable-next-line no-console
            console.warn(
                "Couldn't find rec area info for facilityId:",
                facilityId,
                '\n Not sending Analytics event.'
            );
            return getRecAreaInfoNotFound(facilityId);
        }

        const isRecArea = results[0].entity_type === 'recarea';

        const recAreaInfo = {
            agencyName: results[0].org_name,
            facilityId,
            facilityName: results[0].name,
            recAreaId: isRecArea ? results[0].entity_id : results[0].parent_id,
            recAreaName: isRecArea ? results[0].name : results[0].parent_name,
            stateCode: results[0].state_code,
        };

        fetchCache.set(facilityId, recAreaInfo);
        return recAreaInfo;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching rec area info:', error);
        return getRecAreaInfoNotFound(facilityId);
    }
};

// Cache for fetchRecAreaByInventoryType to prevent duplicate fetches
const inventoryRecAreaCache = new Map<string, RecAreaTrackingInfo>();

/**
 * Fetches rec area information based on inventory type.
 * Uses the appropriate API endpoint for each inventory type to get
 * agency, state, and rec area details for GA tracking.
 *
 * @param facilityId - The facility/location ID
 * @param inventoryType - The inventory type (e.g., 'CAMPING', 'PERMIT', 'TICKET')
 * @returns Promise<RecAreaTrackingInfo> - Standardized rec area info for GA events
 */
export async function fetchRecAreaByInventoryType(
    facilityId: string,
    inventoryType: string
): Promise<RecAreaTrackingInfo> {
    if (!facilityId) {
        return REC_AREA_TRACKING_INFO_DEFAULT;
    }

    const cacheKey = `${inventoryType}_${facilityId}`;
    if (inventoryRecAreaCache.has(cacheKey)) {
        return inventoryRecAreaCache.get(cacheKey) as RecAreaTrackingInfo;
    }

    try {
        let result: RecAreaTrackingInfo = REC_AREA_TRACKING_INFO_DEFAULT;

        // Use search API for all inventory types - it returns org_name, state_code,
        // parent_id, and parent_name which the camping-specific API does not include
        const searchResponse = await fetch(
            `${process.env.API}/search?fq=entity_id:${facilityId}`
        );
        const searchData = await searchResponse.json();
        const searchResults = searchData?.results;

        if (searchResults?.length) {
            const entity = searchResults[0];
            const isRecArea = entity.entity_type === 'recarea';

            result = {
                agency_name: entity.org_name || '',
                state_code: entity.state_code || '',
                rec_area_id: isRecArea ? entity.entity_id || '' : entity.parent_id || '',
                rec_area_name: isRecArea ? entity.name || '' : entity.parent_name || '',
            };
        }

        // Cache the result
        inventoryRecAreaCache.set(cacheKey, result);
        return result;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
            'Could not fetch rec area info for inventory type:',
            inventoryType,
            'facility:',
            facilityId,
            error
        );
        return REC_AREA_TRACKING_INFO_DEFAULT;
    }
}

/**
 * Retrieve recommendation data from sessionStorage for a given item ID.
 * Tries multiple lookup strategies: by itemId, by facility ID (if itemId contains a pipe), and by full productId.
 *
 * @param {string} itemId - The item ID to retrieve recommendation data for (can be facility ID or full productId).
 * @param {string} [productId] - Optional full productId to try as fallback lookup.
 * @returns {Object|null} - The recommendation data or null if not found.
 */
// Helper function to check if recommendation data is expired and clean up if needed
const checkAndCleanExpiredData = (
    data: string | null,
    lookupKey: string
): {
    was_recommended: boolean;
    recommended_location: string;
    previous_booking_dates?: string;
} | null => {
    if (!data) return null;

    try {
        const parsed = JSON.parse(data);
        // Check expiration: 48 hours = 48 * 60 * 60 * 1000 milliseconds
        const expirationTime = 48 * 60 * 60 * 1000;
        const now = Date.now();

        // If timestamp exists, check if expired
        if (parsed.timestamp) {
            if (now - parsed.timestamp < expirationTime) {
                // Still valid
                return {
                    was_recommended: parsed.was_recommended,
                    recommended_location: parsed.recommended_location,
                    ...(parsed.previous_booking_dates && {
                        previous_booking_dates: parsed.previous_booking_dates,
                    }),
                };
            }
            // Expired - clean up
            localStorage.removeItem(lookupKey);
            sessionStorage.removeItem(lookupKey);
            return null;
        }
        // Backward compatibility: entries without timestamp are treated as valid
        // (they may be from before this feature was added)
        return {
            was_recommended: parsed.was_recommended,
            recommended_location: parsed.recommended_location,
            ...(parsed.previous_booking_dates && {
                previous_booking_dates: parsed.previous_booking_dates,
            }),
        };
    } catch (e) {
        // If parsing fails, clean up corrupted data
        localStorage.removeItem(lookupKey);
        sessionStorage.removeItem(lookupKey);
        return null;
    }
};

export const getRecommendationData = (itemId: string, productId?: string) => {
    try {
        // Try lookup by itemId first (this is typically the facilityId during checkout)
        // Try localStorage first (persists across service boundaries), then sessionStorage as fallback
        let lookupKey = `recommendation_${itemId}`;
        let data = localStorage.getItem(lookupKey) || sessionStorage.getItem(lookupKey);
        let result = checkAndCleanExpiredData(data, lookupKey);
        if (result) {
            return result;
        }

        // If not found and itemId contains a pipe (facilityId|inventoryId), try facility ID only
        if (itemId.includes('|')) {
            const facilityId = itemId.split('|')[0];
            lookupKey = `recommendation_${facilityId}`;
            data = localStorage.getItem(lookupKey) || sessionStorage.getItem(lookupKey);
            result = checkAndCleanExpiredData(data, lookupKey);
            if (result) return result;
        }

        // If still not found and productId provided, try extracting facility ID from productId
        if (productId && productId.includes('|')) {
            const facilityIdFromProduct = productId.split('|')[0];
            if (facilityIdFromProduct !== itemId) {
                lookupKey = `recommendation_${facilityIdFromProduct}`;
                data =
                    localStorage.getItem(lookupKey) || sessionStorage.getItem(lookupKey);
                result = checkAndCleanExpiredData(data, lookupKey);
                if (result) return result;
            }
        }

        // If still not found and productId provided, try productId as-is
        if (productId && productId !== itemId) {
            lookupKey = `recommendation_${productId}`;
            data = localStorage.getItem(lookupKey) || sessionStorage.getItem(lookupKey);
            result = checkAndCleanExpiredData(data, lookupKey);
            if (result) return result;
        }

        // Fallback: Check recommendation-refs localStorage key
        // This stores referrer information in a different format: { entityId: { referrer: "...", ... } }
        try {
            const recommendationRefs = localStorage.getItem('recommendation-refs');
            if (recommendationRefs) {
                const refs = JSON.parse(recommendationRefs);
                // Try to find by itemId (facilityId)
                let refData = refs[itemId];
                // If not found and itemId contains a pipe, try facility ID only
                if (!refData && itemId.includes('|')) {
                    const facilityId = itemId.split('|')[0];
                    refData = refs[facilityId];
                }
                // If still not found and productId provided, try extracting facility ID from productId
                if (!refData && productId && productId.includes('|')) {
                    const facilityIdFromProduct = productId.split('|')[0];
                    if (facilityIdFromProduct !== itemId) {
                        refData = refs[facilityIdFromProduct];
                    }
                }
                // If still not found, try productId as-is
                if (!refData && productId && productId !== itemId) {
                    refData = refs[productId];
                }
                // If we found referrer data, ONLY infer recommendation if it contains a recommendation model indicator
                if (
                    refData &&
                    refData.referrer &&
                    isRecommendationReferrer(refData.referrer)
                ) {
                    // Determine location from referrer - if it contains "nearby" or specific patterns
                    let recommendedLocation = 'Explore Nearby Campgrounds';
                    if (refData.referrer.includes('nearby')) {
                        recommendedLocation = 'Explore Nearby Campgrounds';
                    } else if (refData.referrer.includes('campgroundPage')) {
                        recommendedLocation = 'Campground Page';
                    }
                    return {
                        was_recommended: true,
                        recommended_location: recommendedLocation,
                    };
                }
            }
        } catch (e) {
            // Silently fail if recommendation-refs parsing fails
        }

        return null;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return null;
    }
};

const prepareReservationsForStepper = async (reservations: Reservation[]) => {
    const productPromises = reservations.map(async (reservation, idx) => {
        const recAreaInfo = await fetchRecAreaInfo(
            reservation?.order_details?.facility_id ||
                reservation?.order_details?.location_id
        );

        const { recAreaId, recAreaName, stateCode, agencyName, facilityName } =
            recAreaInfo;

        const updatedReservation = {
            ...reservation,
            rec_area_id: recAreaId,
            rec_area_name: recAreaName,
            state_code: stateCode,
            agency_name: agencyName,
            facility_name: facilityName,
        };

        let quantity = updatedReservation?.quantity ?? 1;

        // if unable to find quantity, try to use cart line items as a fallback
        if (!quantity) {
            quantity = updatedReservation?.cart_line_items?.length
                ? updatedReservation.cart_line_items
                      .filter((lineItem) => isInteger(lineItem.quantity))
                      .reduce((prev, next) => prev + next.quantity, 0)
                : 1;
        }

        const name =
            updatedReservation.inventory_type === INVENTORY_POS
                ? updatedReservation.order_details.name
                : updatedReservation.order_details.description;

        const discount = parseFloat(parseFloat(updatedReservation.discount).toFixed(2));
        const subtotal = parseFloat(parseFloat(updatedReservation.subtotal).toFixed(2));
        const price = parseFloat(((subtotal - discount) / quantity).toFixed(2));

        const { productId, category } =
            gaExtractProductIdAndCategoryFromReservation(updatedReservation);

        const inventoryName = getInventoryNameFromReservation(
            category,
            updatedReservation.order_details
        );
        // if typeOfUse exists, we need to format it for GA,
        // and only assign it if it exists
        let typeOfUseData;
        if (updatedReservation.order_details?.camping_type_of_use) {
            typeOfUseData = Array.isArray(
                updatedReservation.order_details?.camping_type_of_use
            )
                ? updatedReservation.order_details?.camping_type_of_use.join(', ')
                : updatedReservation.order_details?.camping_type_of_use;
        }

        // Look up recommendation data for this item
        const facilityId = breakApartAndGetIdFromProductId(productId, 0);
        let recommendationData = getRecommendationData(facilityId, productId);

        // Check for recommendation_referrer in multiple possible locations
        // It may be at the top level or nested in reservation_options
        const recommendationReferrer =
            updatedReservation.recommendation_referrer ||
            (updatedReservation as any).reservation_options?.recommendation_referrer;

        // Fallback: If storage lookup failed (e.g., different origin) but reservation has recommendation_referrer,
        // ONLY infer recommendation if the referrer contains a recommendation model prefix (e.g., "nearby", "nearby2")
        if (
            !recommendationData &&
            recommendationReferrer &&
            isRecommendationReferrer(recommendationReferrer)
        ) {
            recommendationData =
                createRecommendationDataFromReferrer(recommendationReferrer);
        }

        // Additional fallback: Check dataLayer for recent checkout events that had recommendation data
        if (!recommendationData && !recommendationReferrer) {
            recommendationData = findRecommendationDataInDataLayer(productId, facilityId);
        }

        // NOTE - shipping is not standard and cannot be displayed here sadly
        const productObj = gaProductObject({
            index: idx + 1,
            item_brand: process.env.SITE_NAME,
            item_id: productId, // different from sendGAEvent
            item_name: name,
            agency_name: updatedReservation.agency_name,
            state_code: updatedReservation.state_code,
            rec_area_id: updatedReservation.rec_area_id,
            rec_area_name: updatedReservation.rec_area_name,
            facility_id: facilityId,
            facility_name: updatedReservation.facility_name,
            inventory_id: breakApartAndGetIdFromProductId(productId, 1),
            inventory_name: inventoryName, // different from sendGAEvent
            item_category: category,
            quantity,
            price,
            ...(typeof typeOfUseData !== 'undefined' && {
                type_of_use: typeOfUseData,
            }), // only add type_of_use if it exists
            ...(updatedReservation.recommendation_referrer && {
                recommendation_referrer: updatedReservation.recommendation_referrer,
            }), // Add this line
            ...recommendationData, // Include recommendation data if found
        });

        return productObj;
    });

    return Promise.all(productPromises);
};

export const gaReportingCartReservationsForStepper = async (
    reservations: Reservation[],
    steps: Steps,
    trueStep: number,
    checkoutPaymentInfo?: {
        paymentType?: string;
        savedCardOnPurchase?: string;
    } // checkoutPaymentInfo will only be defined on the last step of checkout flow - checkout confirmation
    // recAreaInfo - not sure I want to use it since this component is service agnostic
) => {
    if (!reservations?.length) return null;
    try {
        const orderId = reservations?.[0]?.order_id;
        const products = await prepareReservationsForStepper(reservations);
        const checkoutEvent = gaGenerateCheckoutEvent({
            id: orderId,
            step_number: trueStep + 1,
            items: products,
            checkoutPaymentInfo,
        });

        const buyNowMarker = getBuyNowCheckoutMarker();
        if (checkoutEvent && buyNowMarker) {
            (checkoutEvent as Record<string, unknown>).event_is_buy_now = true;
            if (buyNowMarker.source) {
                (checkoutEvent as Record<string, unknown>).event_buy_now_source =
                    buyNowMarker.source;
            }
            if ((checkoutEvent as { event?: string }).event === 'purchase') {
                clearBuyNowCheckoutMarker();
            }
        }

        return checkoutEvent;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return null;
    }
};

/**
 * This utility function sets a value in session storage for possible downstream google analytics reporting
 *
 * This uses session storage and is very ephemeral, will drop on changing tabs or windows.
 */
export const gaSetRemoveFromCartEvent = () => {
    try {
        // this MUST be handled for Google Analytics, will either be purged or reused
        sessionStorage.setItem(
            CART_GA_PRODUCT_REMOVE_FROM_CART_KEY,
            window?.location?.href
        );
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
    }
};

/**
 * Push a select_item event to Google Analytics data layer for ecommerce tracking.
 *
 * @param {Object} item - The item data to track.
 */
export const gaPushSelectItemEvent = (item: {
    item_id: string;
    item_name: string;
    item_category?: string;
    index: number;
    was_recommended?: boolean;
    recommended_location?: string;
    [key: string]: any;
}) => {
    if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: 'select_item',
            ecommerce: {
                items: [item],
            },
        });
    }

    // Store recommendation data in localStorage (persists across service boundaries on same origin)
    // Also store in sessionStorage as fallback for same-tab persistence
    // Store with both facility ID (for lookup by facility) and full item_id (for lookup by product)
    // Include timestamp for 48-hour expiration
    if (item.was_recommended && item.item_id) {
        try {
            const storageValue = JSON.stringify({
                was_recommended: item.was_recommended,
                recommended_location: item.recommended_location,
                timestamp: Date.now(), // Store timestamp for expiration check
                // Include previous_booking_dates if available (from "Book Again" flow)
                ...(item.previous_booking_dates && {
                    previous_booking_dates: item.previous_booking_dates,
                }),
            });

            // Store with item_id as key (may be facility ID or full product ID)
            const storageKeyByItemId = `recommendation_${item.item_id}`;
            localStorage.setItem(storageKeyByItemId, storageValue);
            sessionStorage.setItem(storageKeyByItemId, storageValue); // Fallback

            // Also store with facility_id if available (for lookup during checkout)
            if (item.facility_id && item.facility_id !== item.item_id) {
                const storageKeyByFacilityId = `recommendation_${item.facility_id}`;
                localStorage.setItem(storageKeyByFacilityId, storageValue);
                sessionStorage.setItem(storageKeyByFacilityId, storageValue); // Fallback
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }
};

/**
 * Custom hook to send Google Analytics events based on item details.
 *
 * @param {Object} params - The parameters for the GA event.
 * @param {string} params.itemLevel - The level of the item (e.g., "Gateway', Facility', 'Inventory', 'Registration Detail').
 * @param {string} params.facilityId - The ID of the facility (used for requesting more information from search api).
 * @param {string} params.itemCategory - The category of the item (e.g., 'CAMPING', 'PERMIT').
 * @param {string} params.itemId - The ID of the item (if not provided, will use inventoryId, or facilityId).
 * @param {string} params.itemName - The name of the item (if not provided, will use inventoryName, or facilityName).
 * @param {string} params.inventoryId - The ID of the inventory (only necessary if `itemLevel === 'Inventory'`).
 * @param {string} params.inventoryName - The name of the inventory (only necessary if `itemLevel === 'Inventory'`).
 * @param {number} params.price - The price of the item (only necessary if `itemLevel === 'Inventory'`).
 * @param {string} [params.eventType='view_item'] - The type of the event (default is 'view_item').
 */
export const useSendGAEvent = ({
    itemLevel,
    facilityId,
    itemCategory,
    itemId,
    itemName,
    inventoryId,
    inventoryName,
    price,
    eventType = 'view_item',
    isDataLoading = false,
    typeOfUse,
}: GAItemEvent) => {
    const wasGaEventFired = useRef(false);
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        if (!facilityId || wasGaEventFired.current || isDataLoading) return;
        try {
            fetchRecAreaInfo(facilityId)
                .then((recAreaInfo) => {
                    if (!isMounted.current) return;
                    const {
                        agencyName,
                        facilityName,
                        recAreaId,
                        recAreaName,
                        stateCode,
                    } = recAreaInfo;

                    // if typeOfUse exists, we need to format it for GA,
                    // and only assign it if it exists
                    let typeOfUseData;
                    if (typeOfUse) {
                        typeOfUseData = Array.isArray(typeOfUse)
                            ? typeOfUse.join(', ')
                            : typeOfUse;
                    }
                    // base itemData
                    const itemData: ProductItem = {
                        index: 1,
                        item_brand: process.env.SITE_NAME,
                        item_id: itemId || inventoryId || facilityId,
                        item_name: itemName || inventoryName || facilityName,
                        item_level: itemLevel,
                        agency_name: agencyName,
                        state_code: stateCode,
                        rec_area_id: recAreaId,
                        rec_area_name: recAreaName,
                        ...(typeof typeOfUseData !== 'undefined' && {
                            type_of_use: typeOfUseData,
                        }), // only add type_of_use if it exists
                    };
                    // for Facility Levels and lower we add facility_id and facility_name
                    if (
                        itemLevel === 'Facility' ||
                        itemLevel === 'Registration Detail' ||
                        itemLevel === 'Pass Selection'
                    ) {
                        itemData.facility_id = facilityId;
                        itemData.facility_name = facilityName;
                        itemData.item_category = itemCategory;
                    } else if (itemLevel === 'Inventory') {
                        // for Inventory Levels we add inventory_id inventory_name
                        itemData.facility_id = facilityId;
                        itemData.facility_name = facilityName;
                        itemData.inventory_id =
                            inventoryId || breakApartAndGetIdFromProductId(itemId, 1);
                        itemData.inventory_name = inventoryName ?? itemName;
                        itemData.item_category = itemCategory;
                        itemData.price = price;
                    }

                    if (window.dataLayer) {
                        window.dataLayer.push({ ecommerce: null });
                        window.dataLayer.push({
                            event: eventType,
                            ecommerce: {
                                items: [itemData],
                            },
                        });
                    }
                    wasGaEventFired.current = true;
                })
                .catch((error) => {
                    if (isMounted.current) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
        return () => {
            isMounted.current = false;
        };
    }, [
        eventType,
        facilityId,
        inventoryId,
        inventoryName,
        itemCategory,
        itemId,
        itemLevel,
        itemName,
        price,
        isDataLoading,
        typeOfUse,
    ]);
};
// Class components in React cannot use hooks because hooks are designed to work only with functional components.
// This wrapper component allows class components to use the `useSendGAEvent` hook by rendering a functional component that uses the hook.
export function SendGAEvent({
    itemLevel, // Must be "Gateway", "Inventory", "Facility", or "Registration Detail"
    facilityId,
    itemCategory,
    itemId,
    itemName,
    inventoryId,
    inventoryName,
    price,
    eventType = 'view_item',
    isDataLoading = false,
    typeOfUse,
}: GAItemEvent) {
    useSendGAEvent({
        itemLevel,
        facilityId,
        itemCategory,
        itemId,
        itemName,
        inventoryId,
        inventoryName,
        price,
        eventType,
        isDataLoading,
        typeOfUse,
    });
    return null;
}

// cspell:ignore ecommerce, recarea, skus
